import { Balance } from '@codex-data/sdk';

import { Issuers } from '@/constants/issuers';

import { RecentToken } from '@/store/search';

import { TokenBase } from '@/utils/0x/token-registry.types';
import { ChainAddress } from '@/utils/models';

import { SearchComboBoxItemProps } from './SearchComboBox/SearchComboBoxItem';
import { PreviewToken } from './TokenPreviewPopover';

export interface TokenWithBalance extends RecentToken {
  balance: string;
  balanceUsd: string;
}

/** Transforms a Codex `Balance` into a TokenWithBalance for display. */
export const mapBalanceItemToTokenResult = (item: Balance): TokenWithBalance | null => {
  if (!item.token || !item.balanceUsd) return null;

  return {
    address: item.tokenAddress as ChainAddress,
    chainId: item.networkId,
    decimals: item.token.decimals,
    name: item.token.name ?? '',
    symbol: item.token.symbol ?? '',
    logo: item.token.info?.imageSmallUrl ?? null,
    balance: item.shiftedBalance.toString(),
    balanceUsd: item.balanceUsd,
  };
};

/** Merges search results with balance data, sorting tokens with balances first by USD value. */
export const mergeSearchResultsWithBalances = (
  searchResults: TokenBase[],
  balances: Balance[] | undefined,
): (TokenBase | (TokenBase & { balance: string; balanceUsd: string }))[] => {
  if (!balances) return searchResults;

  const balanceMap = new Map<string, Balance>();
  for (const balance of balances) {
    const key = `${balance.networkId}-${balance.tokenAddress.toLowerCase()}`;
    balanceMap.set(key, balance);
  }

  return searchResults
    .map((token) => {
      const key = `${token.chainId}-${token.address.toLowerCase()}`;
      const balance = balanceMap.get(key);
      if (!balance || !balance.balanceUsd) return token;
      return {
        ...token,
        balance: balance.shiftedBalance.toString(),
        balanceUsd: balance.balanceUsd,
      };
    })
    .sort((a, b) => {
      const aHasBalance = 'balance' in a && a.balance && a.balanceUsd;
      const bHasBalance = 'balance' in b && b.balance && b.balanceUsd;
      if (aHasBalance && !bHasBalance) return -1;
      if (!aHasBalance && bHasBalance) return 1;
      if (aHasBalance && bHasBalance) {
        return parseFloat(b.balanceUsd!) - parseFloat(a.balanceUsd!);
      }
      return 0;
    });
};

export const getSearchComboBoxItemPropsForTokens = (
  tokens: (TokenBase | TokenWithBalance)[] | undefined,
  setPreviewToken: (token: PreviewToken | null) => void,
): SearchComboBoxItemProps[] | undefined =>
  tokens?.map((token) => ({
    address: token.address,
    chainId: token.chainId,
    name: token.name,
    symbol: token.symbol,
    logoUrl: token.logo ?? undefined,
    balance: 'balance' in token ? token.balance : undefined,
    balanceUsd: 'balanceUsd' in token ? token.balanceUsd : undefined,
    launchpad: token.launchpad?.name,
    verified: 'verified' in token ? token.verified : undefined,
    trusted: 'trusted' in token ? token.trusted : undefined,
    issuer: token.issuer?.name ?? (token.isNativeUsdc ? Issuers.CIRCLE : undefined),
    isSuperstate: token.issuer?.name === Issuers.SUPERSTATE,
    setPreviewToken,
  }));
